import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  OnInit,
  signal,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import {
  ActivityCategoriesState,
  TranslateActivityCategoryPipeModule,
} from '@wilson/activity-categories/data';
import {
  ActivityCategoriesIdsMap,
  LocationAndDateTimeRange,
  DefaultActivityCategory,
} from '@wilson/interfaces';
import { differenceInMinutes } from 'date-fns';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzSegmentedModule, NzSegmentedOption } from 'ng-zorro-antd/segmented';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzTimePickerModule } from 'ng-zorro-antd/time-picker';
import { ShiftTransferTimePickerHelperService } from './shift-transfer-time-picker-helper.service';
import { TransferByTimeComponent } from './transfer-by-time/transfer-by-time.component';
import { TransferPreviewComponent } from './transfer-preview/transfer-preview.component';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { DateTimeFormat } from '@wilson/utils';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faMinus, faPlus } from '@fortawesome/pro-solid-svg-icons';
import { TransferType } from './types';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { UseLocalizedDateFormatDirective } from '@wilson/localization';

enum TransferOption {
  Duration,
  ByTime,
}

@Component({
  selector: 'wilson-add-transfer-modal',
  templateUrl: './add-transfer-modal.component.html',
  styleUrls: ['./add-transfer-modal.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    NzRadioModule,
    NzDividerModule,
    NzTimePickerModule,
    NzInputModule,
    NzDatePickerModule,
    NzSelectModule,
    NzTagModule,
    NzInputNumberModule,
    NzSegmentedModule,
    TransferPreviewComponent,
    TransferByTimeComponent,
    NzTooltipModule,
    FontAwesomeModule,
    NzSpaceModule,
    UseLocalizedDateFormatDirective,
    TranslateActivityCategoryPipeModule,
  ],
  providers: [ShiftTransferTimePickerHelperService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddTransferModalComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly translate = inject(TranslateService);
  protected readonly shiftTransferTimePickerHelperService = inject(
    ShiftTransferTimePickerHelperService,
  );

  public TransferType = TransferType;
  public selectedTransferType = TransferType.Previous;
  public selectedActivityType: string = ActivityCategoriesIdsMap.GuestRide;
  public isNextShiftAvailable = false;
  public isPreviousShiftAvailable = false;
  public isPreviousShiftEditable = false;
  public isNextShiftEditable = false;
  public previousShift!: LocationAndDateTimeRange & {
    shiftId?: string;
  };
  public nextShift!: LocationAndDateTimeRange & {
    shiftId?: string;
  };
  public selectedDateTime!: Date;

  protected activityCategoriesSignal = this.store.selectSignal(
    ActivityCategoriesState.enabledCategoriesSortedByName,
  );

  // Quick access categories for tags
  protected readonly QUICK_ACCESS_CATEGORIES = [
    DefaultActivityCategory.GuestRide,
    DefaultActivityCategory.HomeTransfer,
    DefaultActivityCategory.CarTransfer,
    DefaultActivityCategory.InternalTransfer,
    DefaultActivityCategory.Walk,
  ] as const;

  protected readonly quickAccessCategories = computed(() => {
    const allEnabledCategories = this.activityCategoriesSignal();
    const categoryMap = new Map(
      allEnabledCategories.map((cat) => [cat.name, cat]),
    );
    return this.QUICK_ACCESS_CATEGORIES.flatMap((slug) => {
      const category = categoryMap.get(slug);
      return category ? [category] : [];
    });
  });

  public readonly INCREMENT_DECREMENT_MINUTES = 15;
  public transferOptions: NzSegmentedOption[] = [
    {
      label: this.translate.instant(
        'page.shift_timeline.add_transfer.duration',
      ),
      value: TransferOption.Duration,
    },
    {
      label: this.translate.instant('page.shift_timeline.add_transfer.by_time'),
      value: TransferOption.ByTime,
    },
  ];
  public selectedTransferOption: TransferOption = TransferOption.Duration;
  public readonly maxDurationSignal = signal<number>(0);
  public readonly selectedDurationSignal = signal(60);
  public readonly validateDurationSignal = computed(() => {
    const duration = this.selectedDurationSignal();
    const maxDuration = this.maxDurationSignal();
    if (isNaN(duration) || duration < 0) {
      return 0;
    } else if (duration > maxDuration) {
      return maxDuration;
    } else {
      return duration;
    }
  });

  protected DateTimeFormat = DateTimeFormat;
  protected TransferOption = TransferOption;
  readonly faPlus = faPlus;
  readonly faMinus = faMinus;

  ngOnInit(): void {
    this.shiftTransferTimePickerHelperService.startDateTime =
      this.previousShift.endDateTime;
    this.shiftTransferTimePickerHelperService.endDateTime =
      this.nextShift.startDateTime;

    if (this.isNextShiftAvailable && this.isNextShiftEditable) {
      this.selectedTransferType = TransferType.Next;
    } else if (this.isPreviousShiftAvailable && this.isPreviousShiftEditable) {
      this.selectedTransferType = TransferType.Previous;
    } else {
      this.selectedTransferType = TransferType.New;
    }

    this.maxDurationSignal.set(this.getDefaultDuration());
  }

  public onTransferTypeChange(): void {
    if (this.selectedTransferType === TransferType.New) {
      this.maxDurationSignal.set(
        differenceInMinutes(
          new Date(this.nextShift.startDateTime),
          this.selectedDateTime,
        ),
      );
    } else {
      this.maxDurationSignal.set(this.getDefaultDuration());
    }
  }

  public onStartDateChange(): void {
    this.maxDurationSignal.set(
      differenceInMinutes(
        new Date(this.nextShift.startDateTime),
        this.selectedDateTime,
      ),
    );
  }

  public changeSelectedDateTime(dateTime: Date): void {
    this.selectedDateTime = dateTime;
    this.onStartDateChange();
  }

  public setDuration(defaultTime: number): void {
    this.selectedDurationSignal.set(defaultTime);
  }

  public validateDuration(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const value = parseFloat(inputElement.value);
    this.selectedDurationSignal.set(value);
    inputElement.value = this.validateDurationSignal().toString();
  }
  protected updateTransferOption(value: string | number): void {
    this.selectedTransferOption = value as TransferOption;
  }

  private getDefaultDuration(): number {
    return differenceInMinutes(
      new Date(this.nextShift.startDateTime),
      new Date(this.previousShift.endDateTime),
    );
  }

  public setSelectedActivityType(selectedActivityType: string): void {
    this.selectedActivityType = selectedActivityType;
  }
}
